import React from 'react'
import { useTranslation } from 'react-i18next';
import { TextInput } from '@/components/common/text-input'
import { Dropdown } from '@/components/ui/modal';
import { useToast } from "@/hooks/use-toast";
import { Upload } from '@/components/common/upload';
import { cn } from '@/lib/utils';
import { Plus } from 'lucide-react';
import { CreateOrderFormData } from '@/manager/manager/agent-dashboard-order-manager';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { Image } from '@/components/common/image';
import { TrashIcon } from '@/components/ui/icon';
import { IconButton } from '@/components/common/icon-button';
import { TruncatedText } from '@/components/common/truncated-text';
import { FileBgIcon } from './assets';
import { OrderSeatData } from '@/services/agent-v2-order-seat-list';

const ALLOW_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  '.webp',
  'application/pdf',
  '.docx',
  '.doc',
]

export interface CreateFormProps extends CreateOrderFormData {
  orderSeatList: OrderSeatData[];
  emailError?: string;
  goodsIdError?: string;
  onValueChange?: (data: { name: keyof CreateOrderFormData; value: string | number }) => void;
  uploadLoading: boolean;
  uploadAttachment: (file: File) => Promise<void>;
}

const Label = ({ label, required = true, icon }: { label: string; required?: boolean; icon?: React.ReactNode }) => {
  return (
    <label className='flex w-48 mb-2'>
      {icon}
      <span className='text-sm font-medium text-[rgba(52,64,84,1)] mr-0.5'>{label}</span>
      {required && <span className='text-[rgba(117,66,247,1)]'>*</span>}
    </label>
  )
}

/** 创建订单表单 */
export const CreateForm = ({
  orderSeatList,
  userName,
  email,
  emailError,
  goodsId,
  goodsIdError,
  description,
  orderAttachment = '',
  orderAttachmentName = '',
  orderAttachmentType = '',
  onValueChange,
  uploadLoading,
  uploadAttachment
}: CreateFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleChange = (name: keyof CreateOrderFormData, value: string | number) => {
    onValueChange && onValueChange({ name, value });
  };

  const handleUpload = async (file: File) => {
    await uploadAttachment(file);
  }

  const handleInvalid = () => {
    toast.default({
      title: t("agent_dashboard.user_detail.agent_info.invalid_image"),
      description: t(
        "agent_dashboard.user_detail.agent_info.invalid_image_description"
      ),
    });
  }

  return (
    <div className='w-full h-full space-y-5 flex-shrink-0 '>
      <div className='flex flex-col'>
        <Label label={t('agent_dashboard.order_management.create_modal.name')} required={false} />
        <div>
          <TextInput
            type="text"
            value={userName}
            placeholder={t('agent_dashboard.order_management.create_modal.name_placeholder')}
            className='h-11'
            onChange={(e) => handleChange('userName', e.target.value)}
          />
        </div>
      </div>
      <div className='flex flex-col'>
        <Label label={t('agent_dashboard.order_management.create_modal.email')} />
        <div>
          <TextInput
            type="text"
            value={email}
            placeholder={t('agent_dashboard.order_management.create_modal.email_placeholder')}
            className='h-11'
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!emailError}
            helperText={emailError}
          />
        </div>
      </div>
      <div className='flex flex-col'>
        <Label label={t('agent_dashboard.order_management.create_modal.goods_info')} />
        <div>
          <Dropdown
            value={goodsId?.toString() || ''}
            placeholder={t('agent_dashboard.order_management.create_modal.goods_info_placeholder')}
            className={cn(
              'h-11 w-full font-medium',
              goodsIdError && "border-[rgba(217,45,32,1)] hover:border-[rgba(217,45,32,1)] focus:border-[rgba(217,45,32,1)]"
            )}
            defaultValue='all'
            onValueChange={(value) => handleChange('goodsId', value)}
            width='34.5rem'
            options={orderSeatList?.map((seat) => ({
              value: seat.id,
              label: seat.name,
            }))}
          />
          {goodsIdError && <div className='text-[rgba(217,45,32,1)] text-xs pl-1 pt-0.5'>{goodsIdError}</div>}
        </div>
      </div>
      <div className='flex flex-col'>
        <Label label={t('agent_dashboard.order_management.create_modal.order_remark')} required={false} />
        <div>
          <TextInput
            type="text"
            value={description}
            placeholder={t('agent_dashboard.order_management.create_modal.order_remark_placeholder')}
            className='h-11'
            onChange={(e) => handleChange('description', e.target.value)}
            maxLength={30}
          />
        </div>
      </div>
      <div className='flex flex-col'>
        <Label label={t('agent_dashboard.order_management.create_modal.order_file')} required={false} />
        {!orderAttachment && (
          <Upload
            className={cn(
              "relative flex-1 flex flex-col items-center justify-center overflow-hidden rounded-md mb-2 w-full bg-[rgba(249,250,251,1)] transition-colors cursor-pointer",
              "border-2 border-dashed border-[rgba(208,213,221,1)] hover:border-[rgba(102,112,133,1)]",
            )}
            acceptedFileTypes={ALLOW_FILE_TYPES.join(',')}
            // maxFileSize={ALLOW_IMAGE_MAX_SIZE}
            // minFileSize={ALLOW_IMAGE_MIN_SIZE}
            onUpload={handleUpload}
            onInvalid={handleInvalid}
            disabled={uploadLoading}
          >
            <div className='flex flex-col items-center justify-center py-5'>
              <Plus className='size-8 text-black' />
              <div className='text-center mt-4 text-sm leading-5'>
                <span className='font-semibold'>{t('agent_dashboard.order_management.create_modal.order_file_placeholder1')}</span>
                <span className='text-[rgba(102,112,133,1)]'>
                  {t('agent_dashboard.order_management.create_modal.order_file_placeholder2')} <br />
                  {t('agent_dashboard.order_management.create_modal.order_file_placeholder3')}
                </span>
              </div>
            </div>
            <LoadingOverlay loading={uploadLoading} />
          </Upload>
        )}
        {orderAttachment && (
          <div className='size-[7.625rem] relative'>
            {['png', 'jpeg', 'jpg'].includes(orderAttachmentType || '') && (
              <div className='size-full relative rounded-[12px]'>
                <Image
                  src={orderAttachment}
                  alt={orderAttachmentName}
                  className='size-full rounded-[12px] object-cover'
                />
                <IconButton
                  icon={<TrashIcon className='size-5' />}
                  className='absolute bottom-2 right-2 flex justify-center items-center text-white cursor-pointer  hover:bg-white/20'
                  onClick={() => handleChange('orderAttachment', '')}
                />
                <TruncatedText text={orderAttachmentName} className='w-[100%] text-xs text-[rgba(16,24,40,1)] mt-2' />
              </div>
            )}
            {['pdf', 'doc', 'docx', 'webp'].includes(orderAttachmentType || '') && (
              <div className='size-full relative rounded-[12px]'>
                <div className='relative'>
                  <FileBgIcon />
                  <div className='absolute top-[26px] left-0 px-[4.5px] py-[1px] rounded-sm text-[15px] font-semibold text-white bg-[rgba(55,93,251,1)] tracking-[30%]'>
                    {orderAttachmentType}
                  </div>
                </div>
                <IconButton
                  icon={<TrashIcon className='size-5' />}
                  className='absolute bottom-16 right-7 flex justify-center items-center text-black cursor-pointer  hover:bg-black/10'
                  onClick={() => handleChange('orderAttachment', '')}
                />
                <TruncatedText text={orderAttachmentName} className='w-[100%] text-xs text-[rgba(16,24,40,1)] mt-2' />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
